// src/padplanner/landowner.js
//
// The plan page's Landowner section (S6b2): what it says and what it offers,
// as functions of the server's answers, so the rules are tested without a
// page. The server is admin-api/routes/padplannerLandowner.mjs (S6b1):
//
//   GET  …/plans/:id/landowner-submissions  the cases, their live status, and
//                                           each case's FROZEN findings only
//   GET  …/landowner-relationships          the org's grants and can_submit
//   POST …/revisions/:rev_no/landowner-submissions {owner_org_id}
//
// ── WHAT IS OFFERED IS A HINT; THE SERVER DECIDES ───────────────────────────
//
// Every reason a button is disabled here is one the POST refuses with its own
// code (REFUSAL_COPY in planRefusals.js has the same words), so a race between
// this page and the server ends in a sentence, not a crash.
//
// ── WHICH FINDINGS ARE ON THE MAP ───────────────────────────────────────────
//
// The findings of the latest PadPlanner revision, at or before the one on
// screen, that went to a landowner: viewing v2 shows v2's; working on v3 (a
// draft) still shows v2's, which are what v3 answers; once v3 has been
// submitted its own (none, until the landowner returns it) replace them. A
// finding is the landowner's, kept apart from the plan's internal comments.

/** A case's status, as the operator reads it (the portal's submitter words). */
export const LANDOWNER_STATUS_LABEL = Object.freeze({
  draft: "Draft",
  submitted: "With the landowner",
  changes_requested: "Changes requested",
  approved: "Approved",
});

export const NO_RELATIONSHIP_TEXT = "No landowner relationship — contact the landowner or DreamHammer.";
export const REVOKED_TEXT = "The landowner no longer accepts submissions from your organization — contact the landowner or DreamHammer.";
export const SEAT_TEXT = "Your seat can't submit plans to a landowner.";
export const NOT_SUBMITTABLE_TEXT = "Only an approved or issued version can be submitted to a landowner.";
export const ARCHIVED_TEXT = "This plan is archived and can't be changed.";
export const NOT_SUBMITTED_TEXT = "Not submitted to a landowner.";
export const NOT_SENT_TEXT = "Not sent: equipment, zones, other line types, checks and comments.";

/** A revision's lifecycle status from the approval record's row (planLifecycle's words). */
export function revisionStatus(rev) {
  if (!rev) return null;
  if (!rev.frozen) return "draft";
  if (!rev.decided_status) return "in_review";
  if (rev.decided_status === "changes_requested") return "changes_requested";
  return rev.issued_at ? "issued" : "approved";
}

export const SUBMITTABLE = Object.freeze(["approved", "issued"]);

/** The latest submission of a case: its last row (the server lists them oldest first). */
export const latestSubmission = (c) => c.submissions[c.submissions.length - 1] ?? null;

/**
 * Whether revision `revNo` can go to each landowner, and why not.
 * @returns {{disabledReason: string|null, owners: Array<{id, name, reason: string|null}>}}
 */
export function submitOffer({ relationships, revision, revNo, archived, cases = [] }) {
  const live = (relationships?.relationships ?? []).filter((r) => r.state === "live");
  const owners = live.map((r) => ({ id: r.owner_org_id, name: r.owner_org_name, reason: ownerReason(r.owner_org_id, revNo, cases) }));
  let disabledReason = null;
  if (archived) disabledReason = ARCHIVED_TEXT;
  else if (relationships && relationships.can_submit === false) disabledReason = SEAT_TEXT;
  else if (relationships?.state === "none" || (relationships && !relationships.relationships?.length)) disabledReason = NO_RELATIONSHIP_TEXT;
  else if (relationships && live.length === 0) disabledReason = REVOKED_TEXT;
  else if (!SUBMITTABLE.includes(revisionStatus(revision))) disabledReason = NOT_SUBMITTABLE_TEXT;
  else if (owners.length && owners.every((o) => o.reason)) disabledReason = owners.length === 1 ? owners[0].reason : "Every landowner already has this version or is reviewing another.";
  return { disabledReason, owners };
}

/** Why this owner can't take revision `revNo` now (the POST's already_submitted, landowner_review_open), or null. */
function ownerReason(ownerId, revNo, cases) {
  const mine = cases.filter((c) => c.owner_org_id === ownerId);
  if (mine.some((c) => c.submissions.some((s) => s.rev_no === revNo))) return `v${revNo} has already been submitted to this landowner.`;
  const open = mine.filter((c) => c.status === "submitted").at(-1);
  if (open) return `${open.case_number ?? "Their review"} is with the landowner; wait for their answer before submitting again.`;
  return null;
}

/** The findings on the latest case revision of a case (what "k findings" counts). */
export function latestFindings(c) {
  const last = latestSubmission(c);
  return last ? c.findings.filter((f) => f.plan_review_rev_no === last.plan_review_rev_no) : [];
}

/** "Submitted to Earl O&G · PR-EARL-2026-0003 rev 2 · Changes requested · 3 findings" */
export function caseRowText(c) {
  const last = latestSubmission(c);
  const k = latestFindings(c).length;
  return [
    `Submitted to ${c.owner_org_name}`,
    `${c.case_number ?? "Case unavailable"}${last ? ` rev ${last.plan_review_rev_no}` : ""}`,
    c.status ? (LANDOWNER_STATUS_LABEL[c.status] ?? c.status) : "Status unavailable",
    `${k} finding${k === 1 ? "" : "s"}`,
  ].join(" · ");
}

/**
 * The findings shown on the map and in the list for revision `revNo`: those
 * of the latest PadPlanner revision at or before it that was submitted.
 * @returns {{revNo: number|null, findings: Array}} each finding with its case's number and owner
 */
export function findingsInView(cases, revNo) {
  const revs = cases.flatMap((c) => c.submissions.map((s) => s.rev_no)).filter((n) => n <= revNo);
  if (!revs.length) return { revNo: null, findings: [] };
  const at = Math.max(...revs);
  const findings = cases.flatMap((c) => c.findings
    .filter((f) => f.rev_no === at)
    .map((f) => ({ ...f, case_number: c.case_number, owner_org_name: c.owner_org_name })));
  findings.sort((a, b) => (a.case_number ?? "").localeCompare(b.case_number ?? "") || a.ordinal - b.ordinal);
  return { revNo: at, findings };
}

/**
 * A case the landowner has returned, and what to do next: start a revision,
 * approve it internally, resubmit. Null when no case is in changes_requested.
 */
export function changesRequested(cases, plan) {
  const back = cases.filter((c) => c.status === "changes_requested");
  if (!back.length) return null;
  const c = back[back.length - 1];
  const sentRev = latestSubmission(c)?.rev_no ?? null;
  const next = plan.current_revision > (sentRev ?? 0)
    ? `v${plan.current_revision} is the revision that answers it: approve it internally, then resubmit it to ${c.case_number}.`
    : `Next: start revision ${plan.current_revision + 1}, approve it internally, then resubmit it to ${c.case_number}.`;
  return { case: c, text: `${c.owner_org_name} requested changes on v${sentRev}.`, next };
}

/** What a landowner file holds, from its own features (submitter_tag: pad, road, other). */
export function whatGoes(geojson) {
  const n = { pad: 0, roads: 0, wells: 0, other: 0 };
  for (const f of geojson?.features ?? []) {
    const p = f.properties ?? {};
    if (p.submitter_tag === "pad") n.pad += 1;
    else if (p.submitter_tag === "road") n.roads += 1;
    else if (p.kind === "well") n.wells += 1;
    else n.other += 1;
  }
  return n;
}

const plural = (k, one, many) => `${k} ${k === 1 ? one : many}`;
/** "the pad, 3 access roads and 4 wells" */
export function whatGoesText(n) {
  const parts = [n.pad ? (n.pad === 1 ? "the pad" : plural(n.pad, "pad", "pads")) : "no pad",
    plural(n.roads, "access road", "access roads"), plural(n.wells, "well", "wells")];
  return `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`;
}

/** "Earl O&G · PR-EARL-2026-0003 rev 2", a needs-action row's description. */
export const needsActionWhat = (r) => `${r.owner_org_name} · ${r.case_number ?? "case"} rev ${r.plan_review_rev_no}`;

/* ── the intended landowner (158) ───────────────────────────────────────── */
//
// The landowner a plan requires approval from, named on create or while the
// plan is a draft never submitted to a landowner, from the org's LIVE
// relationships. A hint: the Submit box preselects it and the dashboard says
// when it is owed a version; the box is still where a version's owner is
// chosen. A relationship revoked later leaves the name, and the head says why.

export const INTENDED_SUBMITTED_TEXT = "This plan has been submitted to a landowner, so its intended landowner no longer changes.";
export const INTENDED_FROZEN_TEXT = "The intended landowner changes only while the current version is a draft.";
export const INTENDED_CHOOSE_TEXT = "Choose the landowner, or turn off Requires landowner approval.";
export const RELATIONSHIPS_FAILED_TEXT = "Your landowner relationships couldn't be loaded. Reload the page to try again.";

/** The landowners a plan may name now: the org's live relationships, as {id, name}. */
export const liveLandowners = (relationships) => (relationships?.relationships ?? [])
  .filter((r) => r.state === "live").map((r) => ({ id: r.owner_org_id, name: r.owner_org_name }));

/** Why a plan's intended landowner can't change now, or null (158's trigger, in words). */
export function intendedLocked(plan) {
  if (plan.archived_at) return ARCHIVED_TEXT;
  if (plan.landowner_submitted) return INTENDED_SUBMITTED_TEXT;
  if (plan.revision?.frozen) return INTENDED_FROZEN_TEXT;
  return null;
}

/**
 * The head's line: "Landowner: Earl O&G · not yet submitted", or the latest
 * case with that landowner's status; with why, when the relationship is gone.
 * Null when the plan names no landowner. `cases` null (not read yet, or
 * failed) leaves the status off.
 */
export function intendedLandownerLine(plan, cases) {
  const lo = plan.intended_landowner;
  if (!lo) return null;
  const last = (cases ?? []).filter((c) => c.owner_org_id === lo.org_id).at(-1);
  const status = !cases ? null
    : !last ? "not yet submitted"
      : (LANDOWNER_STATUS_LABEL[last.status] ?? "status unavailable").toLowerCase();
  return {
    text: `Landowner: ${lo.name ?? "Unknown organization"}${status ? ` · ${status}` : ""}`,
    reason: lo.relationship === "revoked" ? REVOKED_TEXT : lo.relationship === "live" ? null : NO_RELATIONSHIP_TEXT,
  };
}

/** A Ready to submit to landowner row's description. */
export const readyWhat = (r) => `To ${r.owner_org_name}`;
